import {
  COPPEL_PERFIL_ALTO_RIESGO_TASA_MAX,
  COPPEL_PERFIL_ALTO_RIESGO_TASA_MIN,
  COPPEL_TASA_DEFAULT,
  COPPEL_TASAS_ENUM,
  coppelIntegracionActiva,
} from './constants'
import { enumTasasDesdeTemplate, resolveOpcionesCampo } from './template'

/** Convierte decimal Abacus (0.099) o porcentaje (9.9) a porcentaje numérico. */
export function tasaDecimalAPorcentaje(valor) {
  const n = parseFloat(String(valor).replace(/,/g, '.'))
  if (Number.isNaN(n)) return null
  if (n > 0 && n <= 1) return Math.round(n * 10000) / 100
  return Math.round(n * 100) / 100
}

/** Convierte porcentaje SOC (9.9) a decimal Abacus (0.099). */
export function tasaPorcentajeADecimal(porcentaje) {
  const pct = tasaDecimalAPorcentaje(porcentaje)
  if (pct == null) return null
  return Math.round(pct * 100) / 10000
}

/** Ajusta un valor al enum permitido por Abacus (el más cercano). */
export function snapTasaAbacus(valor, enumTasas) {
  const allowed = (enumTasas && enumTasas.length ? enumTasas : COPPEL_TASAS_ENUM).slice().sort((a, b) => a - b)
  const decimal =
    typeof valor === 'number' && valor > 0 && valor <= 1
      ? valor
      : tasaPorcentajeADecimal(valor)

  if (decimal == null || !allowed.length) return COPPEL_TASA_DEFAULT

  let best = allowed[0]
  let bestDiff = Math.abs(decimal - best)
  for (let i = 1; i < allowed.length; i += 1) {
    const diff = Math.abs(decimal - allowed[i])
    if (diff < bestDiff) {
      best = allowed[i]
      bestDiff = diff
    }
  }
  return best
}

/** Extrae porcentaje del nombre del perfil de cliente (p. ej. "9.90%" o "Tasa 10.5"). */
export function parseTasaPorcentajeDesdeNombre(nombre) {
  if (!nombre) return null
  const s = String(nombre).trim()
  const pctMatch = s.match(/(\d{1,2}(?:[.,]\d{1,4})?)\s*%/)
  if (pctMatch) {
    return tasaDecimalAPorcentaje(pctMatch[1].replace(',', '.'))
  }
  const tasaMatch = s.match(/tasa\s*[:=]?\s*(\d{1,2}(?:[.,]\d{1,4})?)/i)
  if (tasaMatch) {
    return tasaDecimalAPorcentaje(tasaMatch[1].replace(',', '.'))
  }
  return null
}

function normalizarNombrePerfil(nombre) {
  return String(nombre || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

export function perfilClienteSeleccionado(vm) {
  const id = vm.caso && vm.caso.tasa
  if (id === undefined || id === null || id === '') return null
  const lista = vm.tasas || []
  return lista.find((t) => String(t.Id) === String(id)) || null
}

function tasasEnRango(enumTasas, minPct, maxPct) {
  const lo = Math.min(minPct, maxPct)
  const hi = Math.max(minPct, maxPct)
  return enumTasas.filter((dec) => {
    const pct = tasaDecimalAPorcentaje(dec)
    return pct != null && pct >= lo && pct <= hi
  })
}

/** Descarta decimales que Abacus no acepta (p. ej. 0.105). */
export function filtrarTasasAlEnumAbacus(tasas, enumTasas) {
  const permitidos = new Set(getEnumTasasAbacus(enumTasas).map((v) => parseFloat(v)))
  const out = (tasas || []).filter((t) => permitidos.has(parseFloat(t)))
  return out.length ? out : [COPPEL_TASA_DEFAULT]
}

/**
 * Reglas SOC → Abacus según perfil de cliente (ListaPerfilesCliente).
 * @returns {{ tipo: 'rango', minPct: number, maxPct: number } | { tipo: 'unica', pct: number } | null}
 */
export function clasificarPerfilClienteCoppel(perfil) {
  if (!perfil) return null

  const nombre = normalizarNombrePerfil(perfil.Nombre)

  if (/perfil\s*e\b/.test(nombre) || /alto\s*riesgo/.test(nombre)) {
    return {
      tipo: 'rango',
      minPct: COPPEL_PERFIL_ALTO_RIESGO_TASA_MIN,
      maxPct: COPPEL_PERFIL_ALTO_RIESGO_TASA_MAX,
    }
  }

  if (/perfil\s*d\b/.test(nombre) && (/alto/.test(nombre) || /riesgo/.test(nombre))) {
    return {
      tipo: 'rango',
      minPct: COPPEL_PERFIL_ALTO_RIESGO_TASA_MIN,
      maxPct: COPPEL_PERFIL_ALTO_RIESGO_TASA_MAX,
    }
  }

  if (/perfil\s*a\b|premium/.test(nombre)) {
    return { tipo: 'unica', pct: 9.9 }
  }

  if (/perfil\s*b\b|tasa\s*baja|\bbaja\b/.test(nombre)) {
    return { tipo: 'unica', pct: 9.9 }
  }

  if (/perfil\s*c\b|tasa\s*media|\bmedia\b/.test(nombre)) {
    return { tipo: 'unica', pct: 9.9 }
  }

  return null
}

/** Guarda en la instancia Vue la regla Coppel del perfil actual (llamar al cambiar perfil o tras getTasas). */
export function aplicarReglasCoppelDesdePerfilCliente(vm) {
  if (!coppelIntegracionActiva()) return null
  if (!vm) return null
  const regla = clasificarPerfilClienteCoppel(perfilClienteSeleccionado(vm))
  vm.coppelReglasPerfil = regla
  return regla
}

function tasasDesdeReglaPerfil(regla, enumTasas) {
  if (!regla) return null
  if (regla.tipo === 'rango') {
    const enRango = tasasEnRango(enumTasas, regla.minPct, regla.maxPct)
    return enRango.length ? enRango : null
  }
  if (regla.tipo === 'unica' && regla.pct != null) {
    return [snapTasaAbacus(regla.pct, enumTasas)]
  }
  return null
}

/**
 * Enum de tasas desde plantilla Abacus (cache) o constantes locales.
 * @param {{ conditional_options?: object[], inputs?: object[] }|number[]|null} templateOrEnum
 */
export function getEnumTasasAbacus(templateOrEnum) {
  if (Array.isArray(templateOrEnum) && templateOrEnum.length) {
    return [...new Set(templateOrEnum.map((v) => parseFloat(v)).filter((n) => !Number.isNaN(n)))].sort((a, b) => a - b)
  }
  if (templateOrEnum && templateOrEnum.inputs) {
    const desdeTemplate = enumTasasDesdeTemplate(templateOrEnum, COPPEL_TASAS_ENUM)
    if (desdeTemplate.length) return desdeTemplate
  }
  return COPPEL_TASAS_ENUM.slice()
}

/** Contexto mínimo Abacus para resolver tasas/plazos por combinación. */
export function buildAnswersContextoCoppel(destinoId, esquemaId, mapProducto, mapEsquema) {
  return {
    producto_de_financiamiento: mapProducto(destinoId),
    tipo_de_financiamiento: 'Mercado abierto',
    esquema_de_pago: mapEsquema(esquemaId),
  }
}

/**
 * Tasas válidas para una combinación: intersección plantilla Abacus × reglas SOC (perfil/filtro).
 */
export function resolveTasasCoppelParaCombo(vm, template, answersContext) {
  const enumTasas = getEnumTasasAbacus(template)
  const deseadasPerfil = resolveTasasCoppel(vm, enumTasas)
  const permitidasTemplate = resolveOpcionesCampo(template, 'tasa_de_interes', answersContext)

  if (!permitidasTemplate.length) {
    return filtrarTasasAlEnumAbacus(deseadasPerfil, enumTasas)
  }

  const setPermitidas = new Set(permitidasTemplate.map((v) => parseFloat(v)))
  const intersect = deseadasPerfil.filter((t) => setPermitidas.has(parseFloat(t)))
  if (intersect.length) {
    return filtrarTasasAlEnumAbacus(intersect, enumTasas)
  }
  return filtrarTasasAlEnumAbacus(permitidasTemplate, enumTasas)
}

/**
 * Tasas decimales Abacus a cotizar según contexto SOC.
 * Prioridad: filtro manual → reglas perfil (E/D alto riesgo = rango) → % en nombre → default.
 * @param {Vue} vm
 * @param {{ conditional_options?: object[], inputs?: object[] }|number[]|null} templateOrEnum — plantilla o enum
 * @returns {number[]}
 */
export function resolveTasasCoppel(vm, templateOrEnum) {
  const enumTasas = getEnumTasasAbacus(templateOrEnum)

  const minPct = vm.appliedTasaMin
  const maxPct = vm.appliedTasaMax
  if (minPct != null && maxPct != null) {
    const enRango = tasasEnRango(enumTasas, minPct, maxPct)
    if (enRango.length) return filtrarTasasAlEnumAbacus(enRango, enumTasas)
  }

  const reglaPerfil =
    vm.coppelReglasPerfil || clasificarPerfilClienteCoppel(perfilClienteSeleccionado(vm))
  const tasasPerfil = tasasDesdeReglaPerfil(reglaPerfil, enumTasas)
  if (tasasPerfil && tasasPerfil.length) {
    return filtrarTasasAlEnumAbacus(tasasPerfil, enumTasas)
  }

  const perfil = perfilClienteSeleccionado(vm)
  if (perfil) {
    const pct = parseTasaPorcentajeDesdeNombre(perfil.Nombre)
    if (pct != null) {
      return filtrarTasasAlEnumAbacus([snapTasaAbacus(pct, enumTasas)], enumTasas)
    }
  }

  return filtrarTasasAlEnumAbacus([snapTasaAbacus(COPPEL_TASA_DEFAULT, enumTasas)], enumTasas)
}

export function formatTasaPorcentajeDisplay(decimal) {
  const pct = tasaDecimalAPorcentaje(decimal)
  if (pct == null) return ''
  if (pct === Math.floor(pct)) return `${Math.floor(pct)}%`
  return `${pct.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%`
}
